/**
 * Chat header control: open the current AI thread in LangSmith (new tab).
 *
 * Visibility is gated by the parent (ROOT-only via `isHorizonRootRole`). This component
 * only handles disabled-when-no-thread UX and icon styling for NeoAssistant (circular)
 * vs Chat Assistant (plain IconButton) headers.
 *
 * Grep anchors: langsmith, chat top bar, header icon, PsychologyIcon, ROOT debug link.
 */

import React from "react";
import { IconButton, Tooltip } from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import { buildLangSmithThreadUrl } from "../utils/langsmithUrls";
import { toolbarButtonSx, toolbarIconSize } from "./ticketToolbar/toolbarStyles";

export interface LangSmithThreadLinkButtonProps {
  /** Current thread id; button disables when missing. */
  aiThreadId?: string | number | null;
  /**
   * `circular` — NeoAssistant / ticket / device header (filled round chip).
   * `plain` — Chat Assistant title-bar IconButton row.
   * `toolbar` — a segment of the ticket chat's grouped toolbar.
   */
  variant?: "circular" | "plain" | "toolbar";
  /** Embedded ticket chat uses slightly smaller circular controls. */
  compact?: boolean;
}

/**
 * Icon button that opens `buildLangSmithThreadUrl(aiThreadId)` in a new browser tab.
 */
const LangSmithThreadLinkButton: React.FC<LangSmithThreadLinkButtonProps> = ({
  aiThreadId,
  variant = "circular",
  compact = false,
}) => {
  const url = buildLangSmithThreadUrl(aiThreadId);
  const disabled = !url;
  const tooltip = disabled ? "Open a thread first" : "View in LangSmith";

  const handleClick = () => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (variant === "toolbar") {
    return (
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            size="small"
            onClick={handleClick}
            disabled={disabled}
            aria-label="View in LangSmith"
            sx={toolbarButtonSx(compact)}
          >
            <PsychologyOutlinedIcon sx={{ fontSize: toolbarIconSize(compact) }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  if (variant === "plain") {
    return (
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            onClick={handleClick}
            disabled={disabled}
            aria-label="View in LangSmith"
            size="small"
          >
            <PsychologyIcon sx={{ color: disabled ? "#9CA3AF" : "#7C3AED" }} />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  const size = compact ? 24 : 32;
  const iconSize = compact ? 14 : 18;

  return (
    <Tooltip title={tooltip}>
      <span>
        <IconButton
          size="small"
          onClick={handleClick}
          disabled={disabled}
          aria-label="View in LangSmith"
          sx={{
            backgroundColor: disabled ? "#F3F4F6" : "#F5F3FF",
            borderRadius: "50%",
            width: size,
            height: size,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 0,
            mr: compact ? 0.5 : 1,
            border: disabled ? "1px solid #E5E7EB" : "1px solid #E9D5FF",
            "&:hover": {
              backgroundColor: disabled ? "#F3F4F6" : "#EDE9FE",
              borderColor: disabled ? "#E5E7EB" : "#D8B4FE",
            },
          }}
        >
          <PsychologyIcon sx={{ fontSize: iconSize, color: disabled ? "#9CA3AF" : "#7C3AED" }} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default LangSmithThreadLinkButton;
